// src/api/sessionErrors.ts
/**
 * The one question every axios instance in this folder has to answer for itself: "is this session
 * over, or did the server merely say no?"
 *
 * WHY THIS IS A SEPARATE, PURE MODULE. The rule used to live inside `axiosInstance.ts`, which cannot
 * be imported by a test: it creates an axios instance at module scope (jest 27 cannot resolve axios
 * 1.7.8's ESM entry) and it imports the store (which reads localStorage and builds the state tree as
 * a side effect of being loaded). So the single most consequential predicate in the client — the one
 * that decides whether a member gets signed out — had no test at all. Moving it here, with no
 * imports beyond the equally pure `errors.ts`, is what makes it testable. `axiosInstance.ts` keeps
 * re-exporting `isSessionOver` so `campaignApi` and `invoicingApi` are unaffected.
 */
import { errorBody, httpStatus } from './errors';

/**
 * The `error` values `RestAuthenticationEntryPoint` puts in its JSON body, and the reason this file
 * exports a predicate instead of every instance testing `status === 401`.
 *
 * The server no longer answers a dead token with 401. It answers 403, because the mobile builds
 * already installed on phones log out on 403 and ignore 401 outright, and there is no OTA update —
 * a 401 leaves such a phone holding a token it never clears. The revert to 401 is planned but has
 * to wait for a release to reach users (see RestAuthenticationEntryPoint.AUTH_FAILURE_STATUS).
 *
 * So on the wire 403 now means two unrelated things: "your session is over" and "you are signed in
 * and still may not". The status cannot separate them; the body can. Only the auth entry point
 * writes one of these codes into `error` — an authorisation refusal carries `forbidden`, a step-up
 * refusal carries `mfa_required`, a throttled call carries `rate_limited`, an admin controller's own
 * refusal carries a Slovak sentence, and a rejected CORS preflight carries no JSON at all. None of
 * them can collide with the list below, because the entry point is the only writer of it.
 */
export const AUTH_ERROR_CODES = ['token_expired', 'invalid_token', 'not_authenticated'];

/** Path prefix of the MFA endpoints, used by the transitional guard below. */
const MFA_PATH_PREFIX = '/v1/mfa/';

/** Reads `error` out of whatever shape the body arrived in. Never throws: an unreadable body is
 *  simply not proof of a dead session, and guessing wrong here signs someone out for nothing. */
export const carriesAuthErrorCode = (data: any): boolean => {
    if (typeof data === 'string') {
        // responseType 'text', or a JSON body axios did not parse because of the content type.
        try {
            return carriesAuthErrorCode(JSON.parse(data));
        } catch {
            return false;
        }
    }
    if (data && typeof data === 'object') {
        const code = data.error;
        return typeof code === 'string' && AUTH_ERROR_CODES.indexOf(code) !== -1;
    }
    return false;
};

/**
 * A response BODY — `error.response.data`, not the error — normalised to a plain object.
 *
 * Async because of one caller shape: the invoicing CSV/ZIP export and the invoice PDF are fetched
 * with `responseType: 'blob'`, so their error body is a Blob and reading it is a promise. Reading a
 * Blob does not consume it, so the screen's own error handling still sees the same response.
 * ArrayBuffer bodies are deliberately not decoded — the only instance that requests them
 * short-circuits on the status before the body is ever consulted.
 *
 * Never throws. A body it cannot make sense of comes back as null, and every caller treats null as
 * "no evidence" rather than as any particular verdict.
 */
export const readErrorBody = async (data: any): Promise<any> => {
    if (typeof Blob !== 'undefined' && data instanceof Blob) {
        try {
            return await readErrorBody(await data.text());
        } catch {
            return null;
        }
    }
    if (typeof data === 'string') {
        try {
            const parsed = JSON.parse(data);
            return parsed && typeof parsed === 'object' ? parsed : null;
        } catch {
            return null;
        }
    }
    if (data && typeof data === 'object') {
        return data;
    }
    return null;
};

/**
 * "This session cannot continue and no retry will help" — the one condition every instance in this
 * folder has to react to by clearing the token.
 *
 * 401 qualifies on the status alone: the API returns it only for a missing, expired or invalid
 * token, and it is what this check will see again once AUTH_FAILURE_STATUS goes back to
 * UNAUTHORIZED. 403 qualifies only when the body names one of the auth error codes, which is what
 * keeps "not an admin" from being read as "signed out". Both arms are live at once on purpose:
 * flipping the server constant back needs no change here.
 */
export const isSessionOver = async (error: any): Promise<boolean> => {
    const status = httpStatus(error);
    if (status === 401) {
        return true;
    }
    if (status !== 403) {
        return false;
    }
    return carriesAuthErrorCode(await readErrorBody(error?.response?.data));
};

/**
 * The rule the SHARED `api` instance runs — the instance every ordinary screen in this app goes
 * through, and therefore the predicate that decides whether a member gets signed out.
 *
 * It is `isSessionOver` with one extra arm: a 403 whose body cannot be read as a JSON object, or
 * whose JSON names no `error` string at all, is ALSO treated as a dead session. That arm is what is
 * left of the old blanket "any 403 signs you out". It stays because a session that really is over
 * can be refused by something that writes no JSON body whatsoever — a rejected CORS preflight, an
 * nginx error page, a proxy — and being asked to sign in again is recoverable in one login, while
 * being left on a page that silently refuses every request is not.
 *
 * A 403 that DOES name a code keeps the session: `forbidden` (not allowed at all), `mfa_required`
 * (allowed, but this session has not passed its second factor), `rate_limited`, or any Slovak
 * sentence a controller writes into `error`. Comment moderation always requires step-up, and under
 * the old rule the owner would have been signed out of the entire site by their own admin page.
 *
 * The same shape as the rule the installed mobile build already runs
 * (`goly-esa-mobile/api/axiosInstance.tsx`: a 403 is session-over unless the body carries a code
 * that is not an auth code), so the two clients cannot disagree about what a 403 means.
 */
export const shouldSharedApiLogout = async (error: any): Promise<boolean> => {
    const status = httpStatus(error);
    if (status === 401) {
        return true;
    }
    if (status !== 403) {
        return false;
    }
    const body = await readErrorBody(error?.response?.data);
    if (carriesAuthErrorCode(body)) {
        return true;
    }
    // Nothing readable, or readable but silent about why: no evidence either way, and the safe
    // direction on the shared instance is the one the deployed bundle already takes.
    return typeof body?.error !== 'string';
};

/**
 * A wrong second factor answered by a jar that predates the 400 contract.
 *
 * `TotpController.badCode()` used to answer `401 {"message":"Neplatný alebo expirovaný kód."}` — no
 * `error` field at all. Under `isSessionOver` a 401 is a dead session on the status alone, so a
 * mistyped digit would sign the owner out of the whole site instead of showing "try again". The
 * endpoint has no client today, so the server is moving to `400 {"error":"invalid_code"}`; this
 * guard is what keeps the new UI safe against a server that has not been updated yet, which is the
 * exact ordering this project deploys in (jar and bundle are separate steps).
 *
 * Deliberately narrow — a 401 on `/v1/mfa/*` whose body has a `message` and no `error`. A real dead
 * token reaching these endpoints carries `token_expired`/`invalid_token`/`not_authenticated` in
 * `error` and is therefore untouched by this, and no other path is affected at all.
 */
export const isLegacyBadCodeReply = (error: any): boolean => {
    if (httpStatus(error) !== 401) {
        return false;
    }
    const requestConfig = error?.config;
    const url = `${requestConfig?.baseURL || ''}${requestConfig?.url || ''}`;
    if (url.indexOf(MFA_PATH_PREFIX) === -1) {
        return false;
    }
    const data = errorBody(error);
    if (!data) {
        return false;
    }
    return typeof data.message === 'string' && typeof data.error !== 'string';
};

/**
 * The rule `securityApi` runs: end the session for a genuinely dead token, but never for a wrong
 * code, a refusal, or a throttle.
 *
 * This is `isSessionOver` with the transitional 401 carve-out in front of it. It is a strictly
 * narrower rule, so it can only ever keep someone signed in where the shared rule would have signed
 * them out — the failure direction that matters when the screen in question is the one an admin uses
 * to get their second factor working again.
 */
export const shouldSecurityApiLogout = async (error: any): Promise<boolean> => {
    if (isLegacyBadCodeReply(error)) {
        return false;
    }
    return isSessionOver(error);
};
